/**
 * 충전금 > 사용내역 (/m/cash/list)
 *
 * 충전금 원장(g5_cash)을 한 건에 카드 하나로 보여준다. 날짜가 바뀌는 자리에 연월일 제목(카드 밖), 카드에는 시간만. 잔액 카드도 구역 제목도 없다 —
 * 맨 위 검색 줄(검색칸 · 검색) 아래 칩 한 줄 — 「홀드 금액」(회색 알약, 누르면 수주 홀드/받을 금액 탭 시트 —
 * Balance.js openHoldSheet)과 「조회 조건」(파란 알약 = 목록 거르기). 조회 조건을 누르면 조회 설정 시트가 열리고,
 * 칩을 고른 뒤 [조회]를 눌러야 반영된다(목록 위를 조건이 차지하지 않게).
 * 줄을 누르면 오더 건은 오더 시트(작업내용 + 이 오더의 충전금), 그 밖은 상세 시트가 열린다.
 */
import Util from '../../Common/Util';
import CashBalance from './Cash/Balance';

const PAGE_SIZE = 10;        // 한 번에 받는 줄 수 — 한 화면 분량. 끝에 닿으면 무한스크롤이 이만큼씩 더 받는다 (충전·출금 화면과 같은 값)
// 분류 칩 → 서버 type (CashQueryService::memberCashList). 전체는 보내지 않는다
const TYPE = { order: 1, charge: 2, etc: 3 };
// 충전·출금으로 묶는 원장 사유(rel_table)
const CHARGE = ['innopay', 'redbank', 'deposit', 'sms'];
const WITHDRAW = ['request', 'withdraw'];
// 일시 옆 뱃지 — 조회 설정의 분류 칩과 같은 말 · 같은 갈래(서버 type 1/2/3)
const CAT = { order: '장비대금', charge: '충전금', etc: '기타' };
const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
// 회비 원장(rel_table fee · fee2) → 회원 화면 말. fee 는 충전금에서 매달 차감, fee2 는 카드 장기 선결제(충전금 0원).
// 사유 글자('프로그램사용료' · '회비(카드결제)' …)는 시기마다 달라 표(rel_table)로 가른다
const FEE = { fee: '월회비', fee2: '월회비(장기)' };

export default class Cash {
    initialize() {
        this.$root = $('#clRoot');
        if (!this.$root.length) return;

        const d = this.$root.data();
        // 카드는 그리지 않고(render 를 부르지 않는다) 상세 시트만 쓴다 — 홀드 링크(#clHold)의 [data-sheet] 가 연다
        this.card = new CashBalance({
            $usable: $(), $rows: $('#clHold'),
            balance: { total: d.total, soo_hold: d.sooHold, request: d.request, bal_hold: d.balHold },
        });

        // 조회 조건 — 지금 목록에 적용된 값. 시트에서 고르는 중인 값은 this.pending 에 따로 둔다([조회]를 눌러야 옮겨 온다)
        this.period = '3';      // today | 1 | 3 | 6 (개월) | custom
        this.type = 'all';      // all | order | charge | etc
        this.basis = 'cash';    // cash(거래일) | order(작업일)
        this.range = this._periodRange(this.period);
        this.pending = null;
        this.q = '';            // 검색어 (현장 주소 · 발주자/수주자/소속자 이름 · 내용 · 입금자 이름의 일부)

        this.rows = [];
        this.recordsTotal = 0;
        this.seq = 0;           // 조건을 연달아 바꿀 때 늦게 온 응답을 버린다
        this.loading = false;   // 무한스크롤의 onLoad 가 연달아 불릴 수 있다 — 뒤 페이지 재진입을 막는다

        this.$hist = $('#clHist');
        this.$more = $('#clMore');
        this.$more.on('click', () => this.load());
        // 무한 스크롤 — '더 보기' 자리가 화면에 들어오면 다음 쪽을 자동으로 붙인다(충전·출금 화면과 같은 방식).
        // attach 가 null 이면(IntersectionObserver 미지원) 버튼이 그대로 폴백으로 남는다
        this.scroll = $j.Ui.InfiniteScroll.attach(this.$more[0], { onLoad: () => this.load() });
        if (this.scroll) this.$more.addClass('is-auto');
        this.$hist.on('click', '.cl-row', (e) => this.onClickRow($(e.currentTarget).attr('data-num')));

        $('#clFilterBtn').on('click', () => this.openFilter());
        $('#clPeriod').on('click', '.cl-chip', (e) => this.onClickPeriod($(e.currentTarget).attr('data-v')));
        $('#clType').on('click', '.cl-chip', (e) => this.onClickType($(e.currentTarget).attr('data-v')));
        $('#clBasis').on('click', '.cl-chip', (e) => this.onClickBasis($(e.currentTarget).attr('data-v')));
        $('#clApply').on('click', () => this.onClickApply());
        $('#clSearchForm').on('submit', (e) => { e.preventDefault(); this.onSubmitSearch(); });
        $('#clSearchClear').on('click', () => { $('#clQ').val(''); this.onSubmitSearch(); });

        this.load(true);
    }

    /* ───────────────── 조회 조건 ───────────────── */

    /** 기간 칩 → [시작일, 종료일] (오늘 기준) */
    _periodRange(period) {
        const today = dayjs();
        const start = period === 'today' ? today : today.subtract(Util.int(period), 'month');
        return [start.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
    }

    /** 선택 표시는 .is-on — 'on' 을 쓰면 전역 `button.on`(mobile.css: 폭 100% · 노란 버튼)이 걸린다 */
    _select($group, value) {
        $group.find('.cl-chip').each((i, el) => {
            const on = $(el).attr('data-v') === value;
            $(el).toggleClass('is-on', on).attr('aria-pressed', on ? 'true' : 'false');
        });
    }

    /**
     * 조회 설정 시트 — 화면에 숨겨 둔 #clFilter 덩어리를 시트로 옮겨 보여주고, 닫히면 제자리로 되돌린다
     * (칩의 클릭 핸들러가 그대로 살아 있다). 열 때마다 지금 적용된 조건으로 칩을 맞춘다 — 고르다 닫은 것은 버린다
     */
    openFilter() {
        const $filter = $('#clFilter'), $home = $filter.parent();
        this.pending = { period: this.period, type: this.type, basis: this.basis };
        this._syncFilter();
        $('#clStart').val(this.range[0]);
        $('#clEnd').val(this.range[1]);
        $('#clRangeHint').text('');

        CashBalance.openSheet('조회 설정', ($body) => $body.append($filter.prop('hidden', false)), {
            full: false,
            onClose: () => $home.append($filter.prop('hidden', true)),
        });
    }

    /** 고르는 중인 값(pending)대로 칩 · 날짜칸 · 분류 잠금을 그린다 */
    _syncFilter() {
        const p = this.pending, byWork = p.basis === 'order';
        this._select($('#clPeriod'), p.period);
        this._select($('#clType'), p.type);
        this._select($('#clBasis'), p.basis);
        $('#clRange').prop('hidden', p.period !== 'custom');
        // 작업일 기준은 오더(장비대금) 건만 나온다 — 분류를 '장비대금'에 묶고 잠근다
        $('#clType').toggleClass('is-locked', byWork).find('.cl-chip').prop('disabled', byWork);
    }

    onClickPeriod(period) {
        this.pending.period = period;
        $('#clRangeHint').text('');
        this._syncFilter();
    }

    onClickType(type) {
        if (this.pending.basis === 'order') return;
        this.pending.type = type;
        this._syncFilter();
    }

    onClickBasis(basis) {
        this.pending.basis = basis;
        this.pending.type = basis === 'order' ? 'order' : 'all';
        this._syncFilter();
    }

    /** [조회] — 고른 값을 적용하고 시트를 닫는다. 직접선택이면 날짜부터 본다 */
    onClickApply() {
        const p = this.pending;
        let range = this._periodRange(p.period);
        if (p.period === 'custom') {
            const start = $('#clStart').val(), end = $('#clEnd').val();
            let msg = '';
            if (!start || !end) msg = '시작일과 종료일을 모두 골라 주세요.';
            else if (start > end) msg = '시작일이 종료일보다 늦어요.';
            $('#clRangeHint').text(msg);
            if (msg) return;
            range = [start, end];
        }

        this.period = p.period; this.type = p.type; this.basis = p.basis; this.range = range;
        this._renderFilterText();
        $j.Ui.BS.close();
        this.load(true);
    }

    /**
     * 조건 안내 줄(#clCond) — 기본값(3개월 · 전체 · 거래일)이 아닐 때만 '오늘 · 장비대금 · 거래일로 보는 중'.
     * 조회 버튼이 아이콘뿐이라, 조건이 걸려 있는데 모르고 '내역이 없다'고 읽는 걸 막는다
     */
    _renderFilterText() {
        const isDefault = this.period === '3' && this.type === 'all' && this.basis === 'cash';
        const period = this.period === 'custom'
            ? `${dayjs(this.range[0]).format('M.D')}~${dayjs(this.range[1]).format('M.D')}`
            : { today: '오늘', 1: '1개월', 3: '3개월', 6: '6개월' }[this.period];
        const type = { all: '전체', order: '장비대금', charge: '충전금', etc: '기타' }[this.type];
        $('#clCond')
            .text(`${[period, type, this.basis === 'order' ? '작업일' : '거래일'].join(' · ')}로 보는 중`)
            .prop('hidden', isDefault);
    }

    /** 검색은 [검색]이나 키보드의 완료를 눌러야 받는다 — 글자마다 받지 않는다 */
    onSubmitSearch() {
        this.q = String($('#clQ').val()).trim();
        $('#clSearchClear').prop('hidden', !this.q);
        $('#clQ').trigger('blur');      // 키보드를 내려 결과가 보이게
        this.load(true);
    }

    /* ───────────────── 내역 ───────────────── */

    /** @param {boolean} reset true 면 처음부터 다시 받는다 — 받는 중이어도 새 조건이 이긴다(seq 가 늦은 응답을 버린다) */
    load(reset = false) {
        if (!reset && this.loading) return;
        if (reset) this.rows = [];
        const seq = ++this.seq;
        this.loading = true;
        this.$more.prop('disabled', true);
        if (reset) this.$hist.html('<p class="cl-msg">불러오는 중…</p>');

        $.post('/m/ajax/member/cash/logs', {
            start_date: this.range[0], end_date: this.range[1],
            type: TYPE[this.type] || '', order: this.basis, q: this.q,
            start: this.rows.length, length: PAGE_SIZE,
        })
            .done((res) => {
                if (seq !== this.seq) return;
                this.rows = this.rows.concat(res.data || []);
                this.recordsTotal = Util.int(res.recordsTotal);
                this.render();
            })
            .fail(() => {
                if (seq !== this.seq) return;
                if (!this.rows.length) this.$hist.html('<p class="cl-msg">불러오지 못했어요. 잠시 후 다시 조회해 주세요.</p>');
            })
            .always(() => {
                if (seq !== this.seq) return;
                this.loading = false;
                this.$more.prop('disabled', false);
            });
    }

    render() {
        this.$more.prop('hidden', this.rows.length >= this.recordsTotal);
        // 감시를 다시 건다 — 새 줄을 붙여도 '더 보기' 자리가 그대로 화면 안이면 교차 변화가 없어 다음 쪽이 안 불린다
        if (this.scroll && !this.$more.prop('hidden')) { this.scroll.pause(); this.scroll.resume(); }

        if (!this.rows.length) {
            this.$hist.html(
                '<div class="cx-card cx-empty">'
                + '<svg class="cx-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3.5" width="16" height="17" rx="2.5"/><path d="M8 8.5h8M8 12.5h8M8 16.5h5"/></svg>'
                + '<p class="cx-empty-tt">이 조건에 맞는 내역이 없어요</p>'
                + `<p class="cx-empty-desc">${this.q ? '검색어나 기간을 바꿔 보세요.' : '기간이나 분류를 바꿔 보세요.'}</p></div>`
            );
            return;
        }

        // 한 건 = 카드 하나. 날짜가 바뀌는 자리에 연월일 제목 한 줄(달 제목은 따로 없다) — 카드에는 시간만.
        // 거래일로 묶는다. 들어온 돈과 나간 돈이 섞여 있어 합계는 적지 않는다
        let prev = '';
        this.$hist.html(this.rows.map((row) => {
            const when = dayjs(row.datetime), key = when.format('YYYY-MM-DD');
            const head = key === prev ? '' : `<h3 class="cl-day">${when.format('YYYY.M.D')} <em>(${WEEK[when.day()]})</em></h3>`;
            prev = key;
            return head + this._rowHtml(row);
        }).join(''));
    }

    /**
     * 원장 한 줄을 화면 말로 옮긴다 — [갈래, 머리말, 내용].
     * 갈래(CAT 의 키)는 조회 설정의 분류와 같다: 장비대금(오더 건) · 충전금(입금 · 출금) · 기타(그 밖 전부).
     * 머리말은 장비대금이면 수주/발주(이 회원이 어느 쪽인지 role 로 가른다), 충전금이면 입금/출금,
     * 기타는 사용(나갔거나 0원) / 환급(들어왔다 — 사용료 반환 · 페널티 원복 등).
     * 회비 줄은 머리말 자체가 '월회비' · '월회비(장기)' · '월회비(CMS)' 다.
     *   [장비대금] 수주 작업완료 · [충전금] 입금 입금자 홍길동 · [충전금] 출금 · [기타] 월회비(장기) · [기타] 사용 (그 밖 사유 그대로)
     */
    _describe(row) {
        const table = row.rel_table, action = Util.nvl(row.rel_action, '');
        if (table === 'order') return ['order', { soo: '수주', bal: '발주' }[row.role] || '오더', action];
        if (CHARGE.includes(table)) return ['charge', '입금', row.request_username ? `입금자 ${row.request_username}` : ''];
        if (WITHDRAW.includes(table)) return ['charge', '출금', ''];
        // CMS 회비는 은행에서 따로 빠져 충전금은 그대로다 — 사유 칸에 금액이 들어 있다
        if (table === 'cms') return ['etc', '월회비(CMS)', CashBalance.won(Util.int(action))];
        // 회비 사유는 회원 화면 말(월회비)로 바꿔 머리말 자리에 둔다 — '사용 월회비'처럼 겹치지 않게. 돌려받은 줄만 '환급 월회비'
        if (FEE[table]) return Util.int(row.point) > 0 ? ['etc', '환급', FEE[table]] : ['etc', FEE[table], ''];
        return ['etc', Util.int(row.point) > 0 ? '환급' : '사용', action];
    }

    /**
     * 부호를 붙인 금액 — 색을 쓰지 않고 +/− 로만 가른다.
     * @param {boolean} unit '원'을 붙일지 — 카드 줄은 빼고(폭을 아낀다) 상세 시트는 붙인다
     */
    _signed(point, unit = true) {
        const n = Math.abs(point);
        return (point > 0 ? '+' : (point < 0 ? '−' : '')) + (unit ? CashBalance.won(n) : Util.formatNumber(n));
    }

    _rowHtml(row) {
        const when = dayjs(row.datetime);
        const [cat, label, desc] = this._describe(row);
        // 오더 건은 둘째 줄에 작업일('작업 10.15') · 현장 주소(잔액 왼쪽 칸). 발주자 · 소속자 이름은 싣지 않는다
        const site = row.rel_table === 'order' && row.work_day
            ? `<span class="cl-site">${[`작업 ${dayjs(row.work_day).format('M.D')}`, CashBalance.esc(Util.nvl(row.site_address, ''))].filter(Boolean).join(' · ')}</span>` : '';

        // 꺾쇠는 달지 않는다 — 카드 자체가 누르는 단위라 그 자리를 내용에 준다
        return `<button type="button" class="cx-card cx-row cl-row" data-num="${Util.int(row.num)}">`
            // 날짜는 카드 위 제목(.cl-day)에 있다 — 카드에는 시간만
            + `<span class="cx-when">${when.format('HH:mm')}</span>`
            + `<i class="cl-cat is-${cat}">${CAT[cat]}</i>`
            // 카드 안 금액 · 잔액은 '원'을 붙이지 않는다 — 폭을 아낀다(상세 시트에는 붙는다)
            + `<span class="cx-amt">${this._signed(Util.int(row.point), false)}</span>`
            + `<span class="cl-what">${label ? `<b>${label}</b> ` : ''}${CashBalance.esc(desc)}</span>`
            + `<span class="cl-bal">잔액 <b>${Util.formatNumber(Util.int(row.total_point))}</b></span>`
            + site + `</button>`;
    }

    /* ───────────────── 줄을 눌렀을 때 ───────────────── */

    onClickRow(num) {
        const row = this.rows.find((r) => r.num == num);
        if (!row) return;

        // 오더 건 → 오더 시트 (작업내용 + 이 오더의 충전금 모아보기 + 일지보기)
        if (row.rel_table === 'order') {
            $j.Ui.BS.open({
                target: '$j.F.Mobile.OrderSheet',
                fixedHeight: true,
                data: { ordernum: row.rel_id, logs: 'cash' },
            });
            return;
        }
        this.openDetail(row);
    }

    /** 오더가 아닌 건의 상세 — 위에 이 줄의 요약, 아래에 서버가 풀어 준 사유(getDetail) */
    openDetail(row) {
        const [cat, label, desc] = this._describe(row);
        const title = [CAT[cat], label, CashBalance.esc(desc)].filter(Boolean).join(' · ');
        const when = dayjs(row.datetime).format('YYYY.M.D HH:mm');

        CashBalance.openSheet('내역 상세', ($body) => {
            $body.html(
                // 일시는 제목 위, 잔액은 금액 바로 아래 오른쪽 — 숫자끼리 세로로 맞춘다
                `<div class="cd-sheet">`
                + `<p class="cd-sheet-note is-row is-when">${when}</p>`
                + `<div class="cd-sheet-sum"><span>${title}</span><b>${this._signed(Util.int(row.point))}</b></div>`
                + `<p class="cd-sheet-note is-row is-bal">잔액 ${CashBalance.won(Util.int(row.total_point))}</p>`
                + `<p class="cl-detail">불러오는 중…</p></div>`
            );
            const $detail = $body.find('.cl-detail');
            $.get(`/m/ajax/cash/${Util.int(row.num)}`)
                .done((res) => $detail.text(res.result && res.contents ? res.contents : '상세 내용이 없어요.'))
                .fail(() => $detail.text('불러오지 못했어요. 닫고 다시 열어 주세요.'));
        });
    }
}
